import React, { useState, useEffect, useRef } from 'react';
import { Camera, Check, Clock, MessageCircle, Gift } from 'lucide-react';
import { ConsultationService } from '../services/consultationService';
import { WebhookService } from '../services/webhookService';
import { supabase, startNewConsultationSession } from '../lib/supabase';
import ToastNotification from './ToastNotification';
import { generateSubmissionId } from '../utils/uuid';

const ERROR_MESSAGES = {
  INVALID_TOKEN: 'الرابط غير صحيح. يرجى استخدام الرابط المرسل لك عبر الواتساب',
  USER_NOT_FOUND: 'لم نتمكن من العثور على بياناتك. يرجى التواصل معنا',
  UPLOAD_FAILED: 'فشل رفع الصورة. يرجى المحاولة مرة أخرى',
  BUDGET_REQUIRED: 'يرجى اختيار الميزانية المناسبة',
  SUBMISSION_FAILED: 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى'
};

/**
 * FormFollowUp Component - For users coming from Google Sheet form flow
 * 
 * IMPORTANT: This component handles users who were pre-created by n8n with gsheet_ session tokens.
 * It NEVER creates new users - it only works with existing users from the session_token URL parameter.
 * 
 * Flow distinction:
 * 1. Main consultation flow (WonderBeautiesConsultation):
 *    - Uses browser-generated session IDs (session_...)
 *    - Creates anonymous users on the fly
 *    - Uses uploadSessionImage() which calls getCurrentUser()
 * 
 * 2. Form follow-up flow (THIS component):
 *    - Uses n8n-generated session tokens (gsheet_...)
 *    - User must already exist in database (created by n8n)
 *    - Uses uploadImageForUser() with explicit userId to prevent creating duplicate users
 *    - All operations tied to the existing user from session_token parameter
 */
const FormFollowUp = () => {
  // URL params
  const [sessionToken, setSessionToken] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [channelId, setChannelId] = useState(null);

  // User data
  const [userData, setUserData] = useState(null);
  const [consultationData, setConsultationData] = useState(null);

  // Form state
  const [uploadedImage, setUploadedImage] = useState(null);
  const imageInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetRanges, setBudgetRanges] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Toast notification state for webhook submissions
  const [toastStatus, setToastStatus] = useState(null); // null, 'pending', 'success', 'failed'
  const [toastMessage, setToastMessage] = useState('');
  const [webhookRetryCallback, setWebhookRetryCallback] = useState(null);

  // Extract URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('session_token');
    const contact = urlParams.get('contact_id');
    const channel = urlParams.get('channel_id');

    console.log('📱 [FormFollowUp] URL params extracted:', { token, contact, channel });

    // Validate required parameters
    if (!token || !contact || !channel) {
      console.error('❌ [FormFollowUp] Missing required URL parameters');
      setError(ERROR_MESSAGES.INVALID_TOKEN);
      setLoading(false);
      return;
    }

    // IMPORTANT: Ensure this is a Google Sheet flow session token (gsheet_...)
    // This prevents users from the main flow (session_...) from accidentally using this component
    if (!token.startsWith('gsheet_')) {
      console.error('❌ [FormFollowUp] Invalid session token format. Expected gsheet_ prefix:', token);
      setError(ERROR_MESSAGES.INVALID_TOKEN);
      setLoading(false);
      return;
    }

    console.log('✅ [FormFollowUp] Valid gsheet session token detected');
    setSessionToken(token);
    setContactId(contact);
    setChannelId(channel);
  }, []);

  // Load user data when session token is available
  useEffect(() => {
    if (!sessionToken) return;

    const loadUserData = async () => {
      try {
        setLoading(true);

        console.log('🔄 [FormFollowUp] Loading user data for session token:', sessionToken);

        // ===== LOAD USER DATA FROM DATABASE FIRST =====
        const result = await ConsultationService.getUserBySessionToken(sessionToken);

        if (result.error || !result.data) {
          console.error('❌ Failed to load user:', result.error);
          setError(ERROR_MESSAGES.USER_NOT_FOUND);
          setLoading(false);
          return;
        }

        console.log('✅ User data loaded:', result.data);

        // ===== CHECK COMPLETION STATUS FROM DATABASE =====
        const hasUploadedImage = result.data.images && result.data.images.length > 0;
        const hasBudgetSelected = result.data.consultation && result.data.consultation.budget;
        const isCompleted = hasUploadedImage && hasBudgetSelected;

        // console.log('🔍 [FormFollowUp] Session state check:', {
        //   hasUploadedImage,
        //   hasBudgetSelected,
        //   isCompleted
        // });

        // ===== PRIVACY CHECK: Detect shared link =====
        const submissionKey = `form_submitted_${sessionToken}`;
        const hasLocalStorage = localStorage.getItem(submissionKey);

        if (!hasLocalStorage && isCompleted) {
          // No cookie + consultation complete = Shared link to completed consultation
          // Redirect to protect privacy
          console.log('🔒 Shared link detected (no cookie + completed consultation) - redirecting to main flow');
          window.location.href = '/';
          return; // Stop execution
        }

        if (!hasLocalStorage && !isCompleted) {
          // No cookie + consultation not complete = First-time user from n8n
          // Allow access to form
          console.log('🆕 First-time user from n8n - showing form');
        }

        if (hasLocalStorage) {
          // Has cookie = Original user returning
          console.log('✅ Original user detected (has cookie) - restoring session');
        }

        setUserData(result.data.user);
        setConsultationData(result.data.consultation);

        if (isCompleted) {
          // Show result page (only reachable if user has cookie)
          console.log('✅ Consultation completed - showing result page');
          setSubmitted(true);
        } else {
          // Restore partial progress
          console.log('📝 Consultation in progress - restoring state');
          if (hasUploadedImage) {
            setUploadedImage(result.data.images[0]);
            setUploadSuccess(true);
            console.log('✅ Restored uploaded image from database');

            // Set cookie if image exists but cookie doesn't (e.g., cache cleared)
            if (!hasLocalStorage) {
              localStorage.setItem(submissionKey, 'in_progress');
              console.log('🍪 Cookie restored from database state');
            }
          }
        }

        // Store user ID in localStorage for reference
        localStorage.setItem('wonder_user_id', result.data.user.id);
        console.log('💾 User ID stored in localStorage:', result.data.user.id);

        setLoading(false);
      } catch (err) {
        console.error('❌ Exception loading user:', err);
        setError(ERROR_MESSAGES.USER_NOT_FOUND);
        setLoading(false);
      }
    };

    loadUserData();
  }, [sessionToken]);

  // Load budget ranges
  useEffect(() => {
    const loadBudgetRanges = async () => {
      try {
        const { data, error } = await supabase
          .from('budget_ranges')
          .select('*')
          .eq('is_active', true)
          .order('display_order');

        if (!error && data) {
          setBudgetRanges(data);
        }
      } catch (err) {
        console.error('Error loading budget ranges:', err);
      }
    };

    loadBudgetRanges();
  }, []);

  // Handle image upload
  const handleImageUpload = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    // Validate that we have user data before allowing upload
    if (!userData?.id) {
      setError(ERROR_MESSAGES.USER_NOT_FOUND);
      return;
    }

    try {
      setUploading(true);
      setError(null);
      console.log('📤 Uploading image for user:', userData.id, 'File:', file.name);

      // IMPORTANT: Pass userId directly to ensure image is linked to correct user from form follow-up
      const result = await ConsultationService.uploadImageForUser(file, userData.id, {
        contact_id: contactId,
        channel_id: channelId
      });

      if (result.error) {
        console.error('❌ Upload failed:', result.error);
        setError(ConsultationService.getUploadErrorMessage(result.error));
        setUploading(false);
        return;
      }

      console.log('✅ Image uploaded successfully for user:', userData.id, result.data);
      setUploadedImage(result.data);

      const preparationResult = await ConsultationService.prepareImageValidationForUser(
        userData.id
      );

      if (preparationResult.error || !preparationResult.data?.consultation) {
        throw (
          preparationResult.error ||
          new Error('Failed to prepare image validation for uploaded image')
        );
      }

      setConsultationData(preparationResult.data.consultation);
      setUploadSuccess(true);
      setUploading(false);

      callImageWebhookInBackground({
        userId: userData.id,
        consultationId: preparationResult.data.consultation.id,
        imageValidationRequestId: preparationResult.data.imageValidationRequestId,
        formData: {
          name: userData.name,
          phone: userData.phone,
          skinType: consultationData?.skin_type || '',
          relationshipStatus: consultationData?.relationship_status || '',
          skinSensitivity: consultationData?.skin_sensitivity || [],
          additionalSensitivity: consultationData?.additional_sensitivity || '',
          notSensitive: false
        },
        hasImage: true,
        timestamp: new Date().toISOString(),
        channelParams: {
          contact_id: contactId,
          channel_id: channelId
        }
      });

      // Set localStorage cookie on first interaction (image upload)
      // This marks this browser as the original user
      const submissionKey = `form_submitted_${sessionToken}`;
      if (!localStorage.getItem(submissionKey)) {
        localStorage.setItem(submissionKey, 'in_progress');
        console.log('🍪 Cookie set on first interaction (image upload)');
      }
    } catch (err) {
      console.error('❌ Upload exception:', err);
      setError(ConsultationService.getUploadErrorMessage(err));
      setUploading(false);
    }
  };

  const openImagePicker = () => {
    if (uploading) return;

    if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  // Handle budget selection
  const handleBudgetSelect = (budget) => {
    setSelectedBudget(budget);
    console.log('💰 Budget selected:', budget);
  };

  // Handle contact support - redirects to WhatsApp with pre-written message
  const handleContactSupport = () => {
    const phoneNumber = '9647766870115';
    const message = encodeURIComponent('اريد الاستشارة وكود خصم وندورة');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Handle start new consultation - clears localStorage and redirects to main flow
  // eslint-disable-next-line no-unused-vars
  const handleStartNewConsultation = () => {
    console.log('🔄 Starting new consultation session...');

    // Clear localStorage and get new session ID
    const newSessionId = startNewConsultationSession();

    // Clear FormFollowUp specific localStorage
    if (sessionToken) {
      const submissionKey = `form_submitted_${sessionToken}`;
      localStorage.removeItem(submissionKey);
      console.log('🧹 Cleared FormFollowUp submission flag');
    }

    // Redirect to main consultation flow
    window.location.href = '/';

    console.log('✅ Redirecting to main consultation flow with new session:', newSessionId);
  };

  // Background webhook caller with retry logic
  const callWebhookInBackground = async (userId, webhookData, submissionId) => {
    try {
      const webhookUrl = process.env.REACT_APP_HELP_WEBHOOK_URL;

      if (!webhookUrl) {
        console.warn('⚠️ No webhook URL configured, skipping webhook call');
        setToastStatus(null); // Hide toast
        return;
      }

      // Prepare payload in the EXACT format expected by n8n (with underscores)
      const webhookPayload = {
        event_type: 'consultation_started',
        action: 'consultation_started',
        user_choice: 'start_consultation',
        button_text: 'ابدئي الاستشارة ✨',
        user_id: userId,  // Use user_id with underscore
        consultation_id: webhookData.consultationId,  // Use consultation_id with underscore
        session_data: {
          user_info: webhookData.formData,
          has_uploaded_image: webhookData.hasImage,
          consultation_step: 'consultation_start'
        },
        budget_selection: webhookData.budgetSelection,
        channel_mapping: {
          contact_id: webhookData.channelParams?.contact_id || null,
          channel_id: webhookData.channelParams?.channel_id || null,
          has_channel_info: !!(webhookData.channelParams?.contact_id && webhookData.channelParams?.channel_id)
        },
        metadata: {
          user_agent: navigator.userAgent,
          timestamp: webhookData.timestamp,
          page_url: window.location.href,
          db_save_confirmed: true,
          submission_id: submissionId  // Add submission_id to metadata
        }
      };

      // Call webhook with retry logic
      const result = await WebhookService.callWebhookWithRetry(
        webhookUrl,
        webhookPayload,
        {
          maxRetries: 3,
          submissionId: submissionId,
          onStatusChange: (status, info) => {
            if (status === 'attempting') {
              setToastMessage(`جاري المحاولة ${info.attempt}/${info.maxRetries}...`);
              ConsultationService.updateWebhookStatus(userId, 'pending', info.attempt);
            } else if (status === 'retrying') {
              setToastMessage(`إعادة المحاولة بعد ${info.delay} ثانية...`);
            }
          }
        }
      );

      if (result.success) {
        console.log('✅ Webhook sent successfully after', result.attempts, 'attempt(s)');
        setToastStatus('success');
        setToastMessage('تم حفظ البيانات بنجاح ✅');
        await ConsultationService.updateWebhookStatus(userId, 'sent', result.attempts);

        // Auto-hide success toast after 3 seconds
        setTimeout(() => setToastStatus(null), 3000);
      } else {
        console.error('❌ Webhook failed after all retries:', result.error);
        setToastStatus('failed');
        setToastMessage('فشل الحفظ، يرجى المحاولة مرة أخرى');
        await ConsultationService.updateWebhookStatus(userId, 'failed', result.attempts);

        // Store retry callback
        setWebhookRetryCallback(() => () => callWebhookInBackground(userId, webhookData, submissionId));
      }
    } catch (error) {
      console.error('❌ Exception in callWebhookInBackground:', error);
      setToastStatus('failed');
      setToastMessage('حدث خطأ، يرجى المحاولة مرة أخرى');

      // Store retry callback
      setWebhookRetryCallback(() => () => callWebhookInBackground(userId, webhookData, submissionId));
    }
  };

  // Silent image-analysis webhook trigger for self-care flow.
  // Console logs only (no user-facing status/errors).
  const callImageWebhookInBackground = async (webhookData, submissionId) => {
    try {
      if (!webhookData?.hasImage) {
        console.warn('⚠️ [FormFollowUp] Skipping image webhook: hasImage=false');
        return;
      }

      const result = await WebhookService.callImageUploadWebhook(webhookData, {
        maxRetries: 1,
        submissionId,
        onStatusChange: (status, info) => {
          if (status === 'attempting') {
            console.log(
              `🔄 [FormFollowUp] Image webhook attempt ${info.attempt}/${info.maxRetries}`
            );
          } else if (status === 'retrying') {
            console.warn(
              `⚠️ [FormFollowUp] Image webhook retrying in ${info.delay}s (attempt ${info.nextAttempt})`
            );
          } else if (status === 'failed') {
            console.error(
              `❌ [FormFollowUp] Image webhook failed after ${info.attempts} attempts:`,
              info.error
            );
          }
        }
      });

      if (result.success) {
        console.log(
          `✅ [FormFollowUp] Image webhook sent successfully after ${result.attempts} attempt(s)`
        );
      } else {
        console.error('❌ [FormFollowUp] Image webhook final failure:', result.error);
      }
    } catch (error) {
      console.error('❌ [FormFollowUp] Image webhook exception:', error);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate
    if (!uploadSuccess) {
      setError(ERROR_MESSAGES.UPLOAD_FAILED);
      return;
    }

    if (!selectedBudget) {
      setError(ERROR_MESSAGES.BUDGET_REQUIRED);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      console.log('📤 Submitting consultation...');

      // STEP 1: Generate unique submission ID for idempotency
      const submissionId = generateSubmissionId();
      console.log('🆔 Generated submission_id:', submissionId);

      // STEP 2: Check idempotency - has this submission already been processed?
      const idempotencyCheck = await ConsultationService.checkSubmissionIdempotency(submissionId);
      if (idempotencyCheck.exists) {
        console.log('⚠️ Duplicate submission detected, showing success page');
        setSubmitted(true);
        setSubmitting(false);
        return;
      }

      // STEP 3: Save budget to DB FIRST (with retry logic matching main component pattern)
      // This ensures data is persisted before calling webhook
      console.log('💾 Saving budget to database:', selectedBudget.label_ar);

      let budgetResult = await ConsultationService.updateConsultationBudget(
        userData.id,
        selectedBudget.label_ar
      );

      if (!budgetResult.success) {
        console.warn('⚠️ First budget save attempt failed, retrying once...', budgetResult.error);
        // Immediate retry once (matching main component pattern)
        budgetResult = await ConsultationService.updateConsultationBudget(
          userData.id,
          selectedBudget.label_ar
        );

        if (!budgetResult.success) {
          // STOP - don't proceed or trigger webhook if DB save fails
          console.error('❌ Second budget save attempt failed, blocking submission');
          setError('عذراً، لا نستطيع حفظ بياناتك حالياً. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.');
          setSubmitting(false);
          return; // Exit - user can retry button
        }

        console.log('✅ Retry successful, proceeding with submission');
      }

      console.log('✅ Budget saved and verified in database');

      // STEP 4: Store submission ID in database (marks this consultation as "being processed")
      const storeResult = await ConsultationService.storeSubmissionId(userData.id, submissionId);
      if (!storeResult.success) {
        console.warn('⚠️ Failed to store submission_id, but continuing:', storeResult.error);
      }

      // STEP 5: Store submission status in localStorage to persist across page refreshes
      const submissionKey = `form_submitted_${sessionToken}`;
      localStorage.setItem(submissionKey, 'true');
      console.log('💾 Submission status saved to localStorage');

      // STEP 6: IMMEDIATELY show success page
      setSubmitted(true);
      setSubmitting(false);
      console.log('✅ Data saved - showing success page immediately');

      // STEP 7: Call webhook in background with retry logic and toast notifications
      const webhookData = {
        userId: userData.id,
        consultationId: consultationData?.id || budgetResult.data?.id || null,
        formData: {
          name: userData.name,
          phone: userData.phone,
          skinType: consultationData?.skin_type || '',
          relationshipStatus: consultationData?.relationship_status || '',
          skinSensitivity: consultationData?.skin_sensitivity || [],
          additionalSensitivity: '',
          notSensitive: false
        },
        hasImage: uploadSuccess ? true : false, // Use actual upload success state
        timestamp: new Date().toISOString(),
        budgetSelection: {
          range_id: selectedBudget.id,
          min_amount: selectedBudget.min_amount,
          max_amount: selectedBudget.max_amount,
          label_ar: selectedBudget.label_ar,
          label_en: selectedBudget.label_en
        },
        channelParams: {
          contact_id: contactId,
          channel_id: channelId
        }
      };

      console.log('📦 Webhook payload:', webhookData);

      // Show toast notification
      setToastStatus('pending');
      setToastMessage('جاري حفظ بياناتك...');

      // Call webhook in background (non-blocking)
      callWebhookInBackground(userData.id, webhookData, submissionId);
      callImageWebhookInBackground(webhookData, submissionId);

    } catch (err) {
      console.error('❌ Submission exception:', err);
      setError(ERROR_MESSAGES.SUBMISSION_FAILED);
      setSubmitting(false);
    }
  };

  // Get image URL helper
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    if (typeof image === 'object') {
      // Try common URL properties
      return image.url || image.image_url || image.public_url || image.path || null;
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-[#FFF5F7] flex items-center justify-center p-4" dir="rtl" translate="no">
          <div className="bg-white rounded-2xl p-8 mx-4 shadow-lg max-w-md w-full text-center">
            <div className="text-lg text-[#A73A5D] py-10">جاري التحميل...</div>
          </div>
        </div>
        {/* Toast Notification - Always visible */}
        {toastStatus && (
          <ToastNotification
            status={toastStatus}
            message={toastMessage}
            onRetry={webhookRetryCallback}
            onDismiss={() => setToastStatus(null)}
          />
        )}
      </>
    );
  }

  // Error state
  if (error && !userData) {
    return (
      <>
        <div className="min-h-screen bg-[#FFF5F7] flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl p-8 mx-4 shadow-lg max-w-md w-full text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <p className="text-gray-700">{error}</p>
          </div>
        </div>
        {/* Toast Notification - Always visible */}
        {toastStatus && (
          <ToastNotification
            status={toastStatus}
            message={toastMessage}
            onRetry={webhookRetryCallback}
            onDismiss={() => setToastStatus(null)}
          />
        )}
      </>
    );
  }

  // Success state
  if (submitted) {
    return (
      <>
        <div className="min-h-screen bg-[#FFF5F7] w-full overflow-y-auto" dir="rtl" translate="no">
          <div className="max-w-md mx-auto px-4 py-6 pb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">شكراً لك!</h2>
                <p className="text-gray-600">تم استلام معلوماتك بنجاح</p>
              </div>

              <div className="space-y-4 mb-6 text-center">
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Clock className="w-6 h-6 text-[#A73A5D]" />
                    <h3 className="text-xl font-bold text-gray-900">10 دقائق</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    إحدى صيدلانياتنا المتخصصات ستتواصل معك خلال 10 دقائق لمساعدتك
                  </p>
                </div>

                {/* WhatsApp Discount CTA Section */}
                <div className="bg-gradient-to-br from-[#A73A5D] to-[#8F2F4D] rounded-2xl p-6 shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  <div className="flex items-center justify-center gap-2 mb-5">
                    <Gift className="w-7 h-7 text-white animate-pulse" />
                  </div>
                  <p className="text-white/90 leading-relaxed text-sm mb-5 text-center">
                    تواصلي معنا الآن عبر الواتساب واحصلي على كود خصم خاص لطلبك! ✨
                  </p>
                  <button
                    onClick={handleContactSupport}
                    className="w-full bg-white text-[#A73A5D] py-4 px-6 rounded-xl font-bold text-lg shadow-md hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center justify-center gap-3"
                  >
                    <MessageCircle className="w-6 h-6" />
                    <span>اضغطي هنا للحصول على كود الخصم ✨</span>
                  </button>
                </div>
              </div>

              {/* Contact Info Display */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3 text-center">معلوماتك المسجلة:</h4>
                <div className="space-y-2 text-gray-700">
                  <p className="flex justify-between items-center text-sm">
                    <span className="font-medium">الاسم:</span>
                    <span dir="auto" className="text-right break-words flex-1 ml-2">{userData?.name || 'غير محدد'}</span>
                  </p>
                  <p className="flex justify-between items-center text-sm">
                    <span className="font-medium">الهاتف:</span>
                    <span dir="ltr" className="text-left">{userData?.phone || 'غير محدد'}</span>
                  </p>
                </div>
              </div>

              {/* Start New Consultation - COMMENTED OUT: Customers were misusing this feature */}
              {/* <div className="border-t border-gray-200 pt-5 mt-5">
              <button
                onClick={handleStartNewConsultation}
                className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 p-4 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
              >
                <RefreshCw className="w-5 h-5" />
                ابدئي استشارة جديدة
              </button>
            </div> */}
            </div>
          </div>
          {/* Toast Notification - Always visible */}
          {toastStatus && (
            <ToastNotification
              status={toastStatus}
              message={toastMessage}
              onRetry={webhookRetryCallback}
              onDismiss={() => setToastStatus(null)}
            />
          )}
        </div>
      </>
    );
  }

  // Main form
  const imageUrl = getImageUrl(uploadedImage);

  return (
    <>
      <div className="min-h-screen bg-[#FFF5F7]" dir="rtl" translate="no">
        <div className="p-6 max-w-md mx-auto">
          <div className="space-y-6 animate-fade-in">
            {/* Welcome message */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-[#A73A5D] mb-2">
                مرحباً {userData?.name || 'عزيزتي'}! 🌹
              </h1>
              <p className="text-[#6B4E58]">
                لإكمال استشارتك، يرجى رفع صورة بشرتك واختيار الميزانية المناسبة
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 rounded-xl mb-4 text-red-700 border border-red-200">
                <span className="text-xl">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Image upload section */}
            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-20 h-20 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-[#2D1B1F] mb-2">ارفعي صورة بشرتك</h2>
                <p className="text-[#6B4E58]">سنحلل بشرتك لنقدم لك أفضل العناية</p>
                <p className="text-sm text-[#A73A5D] mt-2 font-medium">
                  صورتك آمنة و سرّية ولا يتم الاحتفاظ بها او نشرها 🙏🏻
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 border border-[#A73A5D]/10 shadow-sm">
                <input
                  ref={imageInputRef}
                  type="file"
                  id="form-followup-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {!uploadSuccess ? (
                  <button
                    type="button"
                    onClick={openImagePicker}
                    disabled={uploading}
                    className="block w-full cursor-pointer group text-right disabled:cursor-not-allowed"
                  >
                    <div className="border-2 border-dashed border-[#A73A5D]/20 rounded-2xl p-12 text-center hover:border-[#A73A5D]/30 transition-all duration-300 group-hover:bg-[#FBF8F9] group-hover:scale-105">
                      <Camera className="w-16 h-16 text-[#A73A5D] mx-auto mb-4" />
                      <p className="text-lg font-semibold text-[#2D1B1F]">
                        {uploading ? 'جاري الرفع...' : 'اضغطي لرفع الصورة'}
                      </p>
                      {!uploading && (
                        <>
                          <p className="text-sm text-[#6B4E58] mt-2">أو اسحبي الصورة هنا</p>
                          <div className="mt-4 flex justify-center gap-2">
                            <div className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
                            <div className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
                          </div>
                        </>
                      )}
                    </div>
                  </button>
                ) : (
                  <div className="text-center space-y-4 animate-fade-in">
                    <div className="relative">
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt="Uploaded skin"
                          className="w-40 h-40 rounded-full mx-auto object-cover border-4 border-[#A73A5D]/20 shadow-md"
                        />
                      )}
                      <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center animate-bounce ${uploadSuccess ? 'bg-green-500' : uploading ? 'bg-yellow-500' : 'bg-red-500'
                        }`}>
                        {uploading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : uploadSuccess ? (
                          <Check className="w-5 h-5 text-white" />
                        ) : (
                          <span className="text-white text-lg">!</span>
                        )}
                      </div>
                    </div>

                    {/* Upload Status Message */}
                    <div className="flex items-center justify-center gap-2 animate-fade-in">
                      {uploading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="font-semibold text-blue-600">جاري رفع الصورة...</span>
                        </>
                      ) : uploadSuccess ? (
                        <>
                          <Check className="w-5 h-5 text-green-600 animate-pulse" />
                          <span className="font-semibold text-green-600">تم رفع الصورة بنجاح!</span>
                        </>
                      ) : (
                        <>
                          <span className="w-5 h-5 text-red-500">⚠️</span>
                          <span className="font-semibold text-red-600">فشل في رفع الصورة</span>
                        </>
                      )}
                    </div>

                    {/* Retry button if upload failed */}
                    {!uploading && !uploadSuccess && (
                      <button
                        type="button"
                        onClick={openImagePicker}
                        className="w-full bg-gradient-to-r from-blue-400 to-blue-500 text-white py-3 rounded-2xl font-bold hover:shadow-lg transition-all duration-300 hover:scale-105"
                      >
                        إعادة المحاولة 🔄
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Budget selection */}
            <div className="bg-white rounded-3xl p-6 border border-[#A73A5D]/10 space-y-6 shadow-sm">
              <div>
                <h3 className="text-lg font-bold text-[#A73A5D] mb-3 flex items-center gap-2">
                  <span className="text-xl">💰</span>
                  اختاري ميزانيتك *
                </h3>
                <p className="text-sm text-[#6B4E58] mb-4">سنختار لك منتجات تناسب ميزانيتك المحددة</p>

                <div className="space-y-3">
                  {budgetRanges.map((range) => (
                    <button
                      key={range.id}
                      onClick={() => handleBudgetSelect(range)}
                      className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-right hover:scale-105 ${selectedBudget?.id === range.id
                        ? 'border-[#A73A5D] bg-[#A73A5D]/10 text-[#A73A5D] shadow-sm'
                        : 'border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30'
                        }`}
                    >
                      <span className="text-lg font-semibold">{range.label_ar}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={!uploadSuccess || !selectedBudget || submitting}
                className={`w-full bg-[#A73A5D] text-white py-4 rounded-2xl font-bold text-lg hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    جاري الإرسال...
                  </span>
                ) : (
                  'ابدئي الاستشارة ✨'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Toast Notification - Always visible */}
      {toastStatus && (
        <ToastNotification
          status={toastStatus}
          message={toastMessage}
          onRetry={webhookRetryCallback}
          onDismiss={() => setToastStatus(null)}
        />
      )}
    </>
  );
};

export default FormFollowUp;

